"use client";

import {useState} from "react";
import Container from "../../../../_components/Container";
import StrapiImage, {
	StrapiImageData,
} from "../../../../_components/StrapiImage";
import Size from "../../../../util/Size";
import wrapWithErrorBoundary from "../../../../util/wrapWithErrorBoundary";
import {ABOUT_TIMELINE_ID} from "../constants";
import clsMerge from "@/app/util/clsMerge";

interface TimelineEntry {
	title: string;
	year: string;
	subtitle: string;
	description: string;
	image: {data: StrapiImageData};
}

interface TimelineContentSectionProps {
	page: number;
	entries: TimelineEntry[];
}

// For the opacity transition to work, we need all the content elements to overlap.
// To make the content elements overlap, we make the parent a grid container,
//   and put all the elemnts into grid area 1/1.
const FIRST_GRID_AREA_CLASS_NAMES =
	"col-start-1 col-end-1 row-start-1 row-end-1";

const TimelineContentSection: React.FC<TimelineContentSectionProps> = ({
	page,
	entries,
}) => {
	return (
		<div className="grid grid-cols-1 border-r-1 border-r-gray-100 pb-8 lg:order-2 lg:border-r-0 lg:pb-0">
			{entries.map(({image, year, subtitle, description, title}, i) => {
				const isSelected = i === page;
				return (
					<div
						key={title}
						className={`flex flex-col gap-10 pr-4 md:pr-8 lg:pr-0 ${FIRST_GRID_AREA_CLASS_NAMES} ${
							isSelected ? "opacity-100" : "opacity-0"
						} transition-opacity duration-300 ease-linear`}
					>
						<div className="relative">
							<StrapiImage image={image.data} width="364" height="243" />
							<div className="absolute bottom-0 right-[5%] translate-y-[50%] text-display-3 text-gray-50 opacity-50 md:text-display-7">
								{year}
							</div>
						</div>
						<div>
							<h6 className="mb-2.5 font-overline text-light-green">
								{subtitle}
							</h6>
							<p className="no-scrollbar overflow-y-scroll text-gray-100 lg:h-auto">
								{description}
							</p>
						</div>
					</div>
				);
			})}
		</div>
	);
};

/**
 * White horizontal line component that fills up the rest of its flex container
 */
const WhiteLine = () => <div className="h-1px grow bg-gray-100" />;

interface TimelineMenuProps {
	page: number;
	setPage: React.Dispatch<React.SetStateAction<number>>;
	entries: TimelineEntry[];
}

const TimelineMenu: React.FC<TimelineMenuProps> = ({
	page,
	setPage,
	entries,
}) => {
	return (
		<div className="flex flex-col justify-center gap-6 overflow-hidden border-r-1 border-r-gray-100 lg:order-1">
			{entries.map((entry, i) => {
				const isSelected = i === page;
				return (
					<button
						key={entry.title}
						className={clsMerge(
							"group flex cursor-pointer items-center gap-6 text-gray-100 ring-0 transition-[padding] focus:border-none focus:shadow-none focus:outline-none",
							isSelected && "text-light-green motion-safe:px-4",
							!isSelected &&
								"hover:px-2 hover:text-green-200 focus:px-2 focus:text-green-200 motion-reduce:hover:px-0 motion-reduce:focus:px-0"
						)}
						onClick={() => setPage(i)}
					>
						<div className="w-16">{entry.year || "--"}</div>
						<h2 className="whitespace-nowrap motion-reduce:decoration-1 motion-reduce:underline-offset-4 motion-reduce:group-hover:underline motion-reduce:group-focus:underline">
							{entry.title}
						</h2>
						{isSelected && <WhiteLine />}
					</button>
				);
			})}
		</div>
	);
};

interface TimelineSectionProps {
	data: {
		entries: TimelineEntry[];
	};
}

const TimelineSection: React.FC<TimelineSectionProps> = ({data: {entries}}) => {
	const [page, setPage] = useState(0);
	return (
		<Container
			id={ABOUT_TIMELINE_ID}
			size={Size.lg}
			className="flex flex-col bg-gray-800 lg:grid lg:grid-cols-2 lg:gap-6"
		>
			<TimelineContentSection page={page} entries={entries} />
			<TimelineMenu page={page} setPage={setPage} entries={entries} />
		</Container>
	);
};

export default wrapWithErrorBoundary(TimelineSection);
